import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  Globe, FileText, Image as ImageIcon, ListTree, Palette, FileEdit, Activity, Rocket,
  Upload, Plus, Check, X, MessageSquare, Trash2, Download, Send, ThumbsUp,
  Loader2, ChevronRight, ExternalLink, AlertCircle, Sparkles
} from 'lucide-react';
import WebsitePaywallModal from './WebsitePaywallModal';
import AddonEntitlements from './AddonEntitlements';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const BUILD_STAGES = [
  { id: 'brief_received', label: 'Brief Received' },
  { id: 'sitemap_approved', label: 'Sitemap Approved' },
  { id: 'design_in_progress', label: 'Design In Progress' },
  { id: 'design_approved', label: 'Design Approved' },
  { id: 'content_approved', label: 'Content Approved' },
  { id: 'development_in_progress', label: 'Development In Progress' },
  { id: 'qa_testing', label: 'QA Testing' },
  { id: 'ready_for_launch', label: 'Ready for Launch' },
  { id: 'launched', label: 'Launched' }
];

const WEBSITE_TYPES = [
  { value: 'new', label: 'New Website' },
  { value: 'redesign', label: 'Redesign' },
  { value: 'landing', label: 'Landing Page' }
];

function StatusBadge({ status }) {
  const map = {
    pending: { label: 'Pending Approval', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
    approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    changes_requested: { label: 'Changes Requested', cls: 'bg-rose-100 text-rose-700 border-rose-200' },
    draft: { label: 'Draft', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
    submitted: { label: 'Submitted', cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' }
  };
  const cfg = map[status] || { label: status, cls: 'bg-slate-100 text-slate-700 border-slate-200' };
  return <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.cls}`}>{cfg.label}</span>;
}

function FeedbackModal({ onClose, onSubmit, title = 'Suggest Changes' }) {
  const [text, setText] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="website-feedback-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">{title}</h3>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4}
          className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-sm resize-none mb-4"
          placeholder="Describe the changes you'd like..." data-testid="website-feedback-text" />
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-medium">Cancel</button>
          <button onClick={() => { if (text.trim()) onSubmit(text); }}
            className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-medium hover:shadow-lg" data-testid="website-feedback-submit">
            Submit Feedback
          </button>
        </div>
      </div>
    </div>
  );
}

function ApprovalActions({ status, feedback, onApprove, onFeedback, isClient }) {
  if (!isClient) {
    return <StatusBadge status={status} />;
  }
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <StatusBadge status={status} />
      {status === 'changes_requested' && feedback && (
        <span className="text-xs text-rose-700 italic">"{feedback}"</span>
      )}
      <button onClick={onApprove} disabled={status === 'approved'}
        className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed">
        <ThumbsUp className="w-3 h-3" />Approve
      </button>
      <button onClick={onFeedback} disabled={status === 'approved'}
        className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-medium hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed">
        <MessageSquare className="w-3 h-3" />Suggest Changes
      </button>
    </div>
  );
}

const MIME = { pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', svg: 'image/svg+xml', gif: 'image/gif', doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', txt: 'text/plain' };

async function downloadFile(fileId) {
  const res = await axios.get(`${API}/website/file/${fileId}`, { headers: authHeaders() });
  const { file_name, file_type, data } = res.data;
  const bytes = atob(data);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  const blob = new Blob([arr], { type: MIME[file_type] || 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = file_name; document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// ────────────────────────────────────────────────────────────────
// 1. PROJECT BRIEF TAB
// ────────────────────────────────────────────────────────────────
function BriefTab({ project, firmId, onChange, isClient, addonEnabled, onPaywall }) {
  const brief = project.brief || {};
  const [form, setForm] = useState(brief);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  useEffect(() => { setForm(brief); /* eslint-disable-next-line */ }, [project.id]);

  const update = (k, v) => setForm({ ...form, [k]: v });
  const updateList = (k, str) => setForm({ ...form, [k]: str.split(',').map(s => s.trim()).filter(Boolean) });

  const buildBriefPayload = () => ({
    website_type: form.website_type,
    current_url: form.current_url || '',
    practice_areas: form.practice_areas || [],
    target_locations: form.target_locations || [],
    competitor_urls: form.competitor_urls || [],
    design_preferences: form.design_preferences || '',
    required_pages: form.required_pages || [],
    notes: form.notes || ''
  });

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/website/${firmId}/brief`, buildBriefPayload(), { headers: authHeaders() });
      onChange();
    } finally { setSaving(false); }
  };
  const submit = async () => {
    // Always save draft first so the brief content is captured even when paywalled
    await save();
    if (isClient && !addonEnabled) {
      // Persist current form in localStorage so it survives Stripe redirect
      try {
        localStorage.setItem(
          `website_creation_pending_brief:${firmId}`,
          JSON.stringify({ saved_at: new Date().toISOString(), brief: buildBriefPayload() })
        );
      } catch (_e) { /* ignore quota errors */ }
      onPaywall && onPaywall();
      return;
    }
    await axios.post(`${API}/website/${firmId}/brief/submit`, {}, { headers: authHeaders() });
    setSubmitted(true);
    onChange();
  };

  return (
    <div className="space-y-4" data-testid="website-brief-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Tell us what you need - we'll plan, design, and build the rest.</p>
        <StatusBadge status={brief.status || 'draft'} />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <div>
          <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Website Type</label>
          <div className="grid sm:grid-cols-3 gap-2 mt-2">
            {WEBSITE_TYPES.map(t => (
              <button key={t.value} type="button" onClick={() => update('website_type', t.value)}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium border-2 transition-all ${form.website_type === t.value ? 'border-violet-500 bg-violet-50 text-slate-900' : 'border-slate-200 hover:border-slate-300 text-slate-700'}`}
                data-testid={`brief-type-${t.value}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Current Website URL (optional)</label>
            <input value={form.current_url || ''} onChange={(e) => update('current_url', e.target.value)} type="url"
              placeholder="https://yourfirm.com"
              className="mt-2 w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Practice Areas / Services <span className="text-slate-400">(comma-separated)</span></label>
            <input value={(form.practice_areas || []).join(', ')} onChange={(e) => updateList('practice_areas', e.target.value)}
              placeholder="Personal Injury, Family Law"
              className="mt-2 w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Target Locations</label>
            <input value={(form.target_locations || []).join(', ')} onChange={(e) => updateList('target_locations', e.target.value)}
              placeholder="London, Manchester"
              className="mt-2 w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Competitor Websites</label>
            <input value={(form.competitor_urls || []).join(', ')} onChange={(e) => updateList('competitor_urls', e.target.value)}
              placeholder="https://competitor1.com, https://competitor2.com"
              className="mt-2 w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30" />
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Required Pages</label>
          <input value={(form.required_pages || []).join(', ')} onChange={(e) => updateList('required_pages', e.target.value)}
            placeholder="Home, About, Services, Contact"
            className="mt-2 w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30" />
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Design Preferences</label>
          <textarea value={form.design_preferences || ''} onChange={(e) => update('design_preferences', e.target.value)} rows={3}
            placeholder="Look & feel, colours, tone, references..."
            className="mt-2 w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 resize-none" />
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Notes</label>
          <textarea value={form.notes || ''} onChange={(e) => update('notes', e.target.value)} rows={3}
            placeholder="Anything else our team should know..."
            className="mt-2 w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 resize-none" />
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <button onClick={save} disabled={saving} className="px-5 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-200 disabled:opacity-50" data-testid="brief-save-btn">
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          <button onClick={submit} disabled={saving} className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl text-sm font-semibold hover:shadow-lg disabled:opacity-50 inline-flex items-center gap-2" data-testid="brief-submit-btn">
            <Send className="w-4 h-4" />{submitted || brief.status === 'submitted' ? 'Resubmit Brief' : 'Submit Brief'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// 2. ASSETS TAB
// ────────────────────────────────────────────────────────────────
function AssetsTab({ project, firmId, onChange, isClient }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const assets = project.assets || [];

  const handleFiles = async (files) => {
    setUploading(true);
    for (const f of files) {
      const fd = new FormData(); fd.append('file', f);
      try {
        await axios.post(`${API}/website/${firmId}/assets`, fd, { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } });
      } catch (e) { console.error(e); }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
    onChange();
  };
  const remove = async (id) => {
    await axios.delete(`${API}/website/${firmId}/assets/${id}`, { headers: authHeaders() });
    onChange();
  };

  return (
    <div className="space-y-4" data-testid="website-assets-tab">
      <p className="text-sm text-slate-500">Logo, brand guidelines, photos, testimonials, content files - upload anything that helps the design team.</p>

      <label className="flex flex-col items-center justify-center border-2 border-dashed border-violet-300 hover:border-violet-500 bg-violet-50/40 hover:bg-violet-50 rounded-2xl py-10 cursor-pointer">
        <Upload className="w-8 h-8 text-violet-500 mb-2" />
        <span className="text-sm font-medium text-slate-700">{uploading ? 'Uploading...' : 'Click to upload assets'}</span>
        <span className="text-xs text-slate-500 mt-1">PDF, DOC, DOCX, TXT, PNG, JPG, WEBP, SVG, GIF (10MB each)</span>
        <input ref={inputRef} type="file" multiple className="hidden" disabled={uploading}
          accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.webp,.svg,.gif"
          onChange={(e) => handleFiles(Array.from(e.target.files || []))}
          data-testid="website-asset-input" />
      </label>

      {assets.length === 0 ? (
        <div className="text-sm text-slate-500 text-center py-6">No assets uploaded yet.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="website-assets-grid">
          {assets.map(a => (
            <div key={a.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                {['png', 'jpg', 'jpeg', 'webp', 'svg', 'gif'].includes(a.file_type) ? <ImageIcon className="w-4 h-4 text-violet-600" /> : <FileText className="w-4 h-4 text-violet-600" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900 truncate">{a.file_name}</div>
                <div className="text-[10px] text-slate-500">By {a.uploaded_by} • {new Date(a.uploaded_at).toLocaleDateString()}</div>
              </div>
              <button onClick={() => downloadFile(a.id)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500"><Download className="w-4 h-4" /></button>
              {!isClient && <button onClick={() => remove(a.id)} className="p-1.5 hover:bg-rose-50 rounded-lg text-rose-500"><Trash2 className="w-4 h-4" /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// 3. SITEMAP TAB
// ────────────────────────────────────────────────────────────────
function SitemapNode({ item, depth = 0 }) {
  return (
    <div style={{ marginLeft: depth * 20 }} className="py-1.5">
      <div className="flex items-center gap-2 text-sm text-slate-800">
        <ChevronRight className="w-3 h-3 text-slate-400" />
        <span className="font-medium">{item.title}</span>
      </div>
      {(item.children || []).map((c, i) => <SitemapNode key={i} item={c} depth={depth + 1} />)}
    </div>
  );
}

function SitemapTab({ project, firmId, onChange, isClient }) {
  const sitemap = project.sitemap || { items: [], status: 'pending', feedback: '' };
  const [editText, setEditText] = useState(JSON.stringify(sitemap.items, null, 2));
  const [saving, setSaving] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setEditText(JSON.stringify(sitemap.items, null, 2)); /* eslint-disable-next-line */ }, [project.id, sitemap.updated_at]);

  const saveSitemap = async () => {
    setError('');
    let parsed;
    try { parsed = JSON.parse(editText); }
    catch { setError('Invalid JSON. Use [{"title":"Home","children":[]}] format.'); return; }
    if (!Array.isArray(parsed)) { setError('Sitemap must be an array.'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/website/${firmId}/sitemap`, { items: parsed }, { headers: authHeaders() });
      onChange();
    } finally { setSaving(false); }
  };

  const approve = async () => {
    await axios.post(`${API}/website/${firmId}/sitemap/approve`, {}, { headers: authHeaders() });
    onChange();
  };
  const sendFeedback = async (text) => {
    await axios.post(`${API}/website/${firmId}/sitemap/feedback`, { feedback: text }, { headers: authHeaders() });
    setShowFeedback(false);
    onChange();
  };

  return (
    <div className="space-y-4" data-testid="website-sitemap-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-slate-500">The proposed structure of your new website.</p>
        <ApprovalActions status={sitemap.status} feedback={sitemap.feedback}
          onApprove={approve} onFeedback={() => setShowFeedback(true)} isClient={isClient} />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        {sitemap.items.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-6">Your dedicated team will propose a sitemap shortly.</div>
        ) : (
          <div data-testid="sitemap-tree">{sitemap.items.map((it, i) => <SitemapNode key={i} item={it} />)}</div>
        )}
      </div>

      {!isClient && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-slate-900">Edit Sitemap (JSON)</h4>
            <button onClick={saveSitemap} disabled={saving} className="px-4 py-2 bg-violet-600 text-white rounded-lg text-xs font-medium hover:bg-violet-700 disabled:opacity-50" data-testid="sitemap-save-btn">
              {saving ? 'Saving...' : 'Save & Send for Approval'}
            </button>
          </div>
          <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={10}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-violet-500/30"
            data-testid="sitemap-json-editor" />
          {error && <div className="text-xs text-rose-600 mt-2">{error}</div>}
        </div>
      )}

      {showFeedback && <FeedbackModal onClose={() => setShowFeedback(false)} onSubmit={sendFeedback} title="Suggest sitemap changes" />}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// 4. DESIGN TAB
// ────────────────────────────────────────────────────────────────
function DesignTab({ project, firmId, onChange, isClient }) {
  const designs = project.designs || [];
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [feedbackTarget, setFeedbackTarget] = useState(null);
  const [previews, setPreviews] = useState({}); // {fileId: dataUrl}

  // Lazy-load preview thumbnails
  useEffect(() => {
    const fetchPreviews = async () => {
      for (const d of designs) {
        if (previews[d.id] || !['png', 'jpg', 'jpeg', 'webp', 'svg', 'gif'].includes(d.file_type)) continue;
        try {
          const res = await axios.get(`${API}/website/file/${d.id}`, { headers: authHeaders() });
          setPreviews(p => ({ ...p, [d.id]: `data:${MIME[d.file_type] || 'image/png'};base64,${res.data.data}` }));
        } catch (e) { console.error(e); }
      }
    };
    fetchPreviews();
    /* eslint-disable-next-line */
  }, [designs.length]);

  const handleFiles = async (files) => {
    setUploading(true);
    for (const f of files) {
      const fd = new FormData(); fd.append('file', f); fd.append('label', f.name.split('.')[0]);
      try { await axios.post(`${API}/website/${firmId}/designs`, fd, { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } }); }
      catch (e) { console.error(e); }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
    onChange();
  };
  const remove = async (id) => { await axios.delete(`${API}/website/${firmId}/designs/${id}`, { headers: authHeaders() }); onChange(); };
  const approve = async (id) => { await axios.post(`${API}/website/${firmId}/designs/${id}/approve`, {}, { headers: authHeaders() }); onChange(); };
  const sendFeedback = async (text) => {
    await axios.post(`${API}/website/${firmId}/designs/${feedbackTarget}/feedback`, { feedback: text }, { headers: authHeaders() });
    setFeedbackTarget(null);
    onChange();
  };

  return (
    <div className="space-y-4" data-testid="website-design-tab">
      <p className="text-sm text-slate-500">Homepage, inner pages, and mobile previews. Approve or suggest changes per design.</p>

      {!isClient && (
        <label className="flex items-center justify-center gap-2 border-2 border-dashed border-violet-300 hover:border-violet-500 bg-violet-50/40 hover:bg-violet-50 rounded-2xl py-6 cursor-pointer">
          <Upload className="w-5 h-5 text-violet-600" />
          <span className="text-sm font-medium text-slate-700">{uploading ? 'Uploading...' : 'Upload design mockup (PNG/JPG/PDF)'}</span>
          <input ref={inputRef} type="file" multiple className="hidden" disabled={uploading}
            accept=".png,.jpg,.jpeg,.webp,.svg,.pdf"
            onChange={(e) => handleFiles(Array.from(e.target.files || []))}
            data-testid="design-upload-input" />
        </label>
      )}

      {designs.length === 0 ? (
        <div className="text-sm text-slate-500 text-center py-8 bg-white rounded-2xl border border-slate-200">No designs uploaded yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="design-grid">
          {designs.map(d => (
            <div key={d.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="aspect-video bg-slate-100 flex items-center justify-center overflow-hidden">
                {previews[d.id] ? (
                  <img src={previews[d.id]} alt={d.label} className="w-full h-full object-cover" />
                ) : (
                  <Palette className="w-10 h-10 text-slate-300" />
                )}
              </div>
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{d.label || d.file_name}</div>
                    <div className="text-[10px] text-slate-500">{d.file_name}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => downloadFile(d.id)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500"><Download className="w-4 h-4" /></button>
                    {!isClient && <button onClick={() => remove(d.id)} className="p-1.5 hover:bg-rose-50 rounded-lg text-rose-500"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </div>
                <ApprovalActions status={d.status} feedback={d.feedback}
                  onApprove={() => approve(d.id)} onFeedback={() => setFeedbackTarget(d.id)} isClient={isClient} />
              </div>
            </div>
          ))}
        </div>
      )}

      {feedbackTarget && <FeedbackModal onClose={() => setFeedbackTarget(null)} onSubmit={sendFeedback} title="Suggest design changes" />}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// 5. CONTENT TAB
// ────────────────────────────────────────────────────────────────
function ContentTab({ project, firmId, onChange, isClient }) {
  const items = project.content_items || [];
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ page_name: '', content: '', meta_title: '', meta_description: '' });
  const [feedbackTarget, setFeedbackTarget] = useState(null);

  const addItem = async () => {
    if (!draft.page_name) return;
    await axios.post(`${API}/website/${firmId}/content`, draft, { headers: authHeaders() });
    setDraft({ page_name: '', content: '', meta_title: '', meta_description: '' });
    setAdding(false);
    onChange();
  };
  const remove = async (id) => { await axios.delete(`${API}/website/${firmId}/content/${id}`, { headers: authHeaders() }); onChange(); };
  const approve = async (id) => { await axios.post(`${API}/website/${firmId}/content/${id}/approve`, {}, { headers: authHeaders() }); onChange(); };
  const sendFeedback = async (text) => {
    await axios.post(`${API}/website/${firmId}/content/${feedbackTarget}/feedback`, { feedback: text }, { headers: authHeaders() });
    setFeedbackTarget(null); onChange();
  };

  return (
    <div className="space-y-4" data-testid="website-content-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-slate-500">Review the copy our team has written for each page, then approve or suggest changes.</p>
        {!isClient && (
          <button onClick={() => setAdding(!adding)} className="inline-flex items-center gap-2 px-3 py-2 bg-violet-600 text-white rounded-lg text-xs font-medium hover:bg-violet-700" data-testid="content-add-btn">
            <Plus className="w-4 h-4" /> Add Page Content
          </button>
        )}
      </div>

      {adding && !isClient && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3" data-testid="content-add-form">
          <input value={draft.page_name} onChange={(e) => setDraft({ ...draft, page_name: e.target.value })} placeholder="Page name (e.g. Home)"
            className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm" />
          <input value={draft.meta_title} onChange={(e) => setDraft({ ...draft, meta_title: e.target.value })} placeholder="Meta title"
            className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm" />
          <input value={draft.meta_description} onChange={(e) => setDraft({ ...draft, meta_description: e.target.value })} placeholder="Meta description"
            className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm" />
          <textarea value={draft.content} onChange={(e) => setDraft({ ...draft, content: e.target.value })} rows={5} placeholder="Page content..."
            className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm resize-none" />
          <div className="flex justify-end gap-2">
            <button onClick={() => setAdding(false)} className="px-4 py-2 text-slate-600 bg-slate-100 rounded-lg text-sm">Cancel</button>
            <button onClick={addItem} className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium" data-testid="content-add-save">Save</button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-sm text-slate-500 text-center py-8 bg-white rounded-2xl border border-slate-200">No content drafted yet.</div>
      ) : (
        <div className="space-y-3" data-testid="content-list">
          {items.map(it => (
            <div key={it.id} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-slate-900">{it.page_name}</div>
                  {it.meta_title && <div className="text-xs text-slate-500 mt-1"><span className="font-medium">Meta title:</span> {it.meta_title}</div>}
                  {it.meta_description && <div className="text-xs text-slate-500"><span className="font-medium">Meta description:</span> {it.meta_description}</div>}
                </div>
                {!isClient && (
                  <button onClick={() => remove(it.id)} className="p-1.5 hover:bg-rose-50 rounded-lg text-rose-500"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
              <div className="bg-slate-50 rounded-xl p-3 text-sm text-slate-700 whitespace-pre-wrap">{it.content || <span className="text-slate-400">(no content yet)</span>}</div>
              <ApprovalActions status={it.status} feedback={it.feedback}
                onApprove={() => approve(it.id)} onFeedback={() => setFeedbackTarget(it.id)} isClient={isClient} />
            </div>
          ))}
        </div>
      )}

      {feedbackTarget && <FeedbackModal onClose={() => setFeedbackTarget(null)} onSubmit={sendFeedback} title="Suggest content changes" />}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// 6. BUILD PROGRESS TAB
// ────────────────────────────────────────────────────────────────
function BuildProgressTab({ project, firmId, onChange, isClient }) {
  const currentIdx = BUILD_STAGES.findIndex(s => s.id === project.build_status);
  const pct = currentIdx >= 0 ? Math.round(((currentIdx + 1) / BUILD_STAGES.length) * 100) : 0;
  const setStatus = async (id) => {
    await axios.patch(`${API}/website/${firmId}/build-status`, { build_status: id }, { headers: authHeaders() });
    onChange();
  };

  return (
    <div className="space-y-4" data-testid="website-progress-tab">
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs text-slate-500">Current stage</div>
            <div className="text-lg font-bold text-slate-900">{BUILD_STAGES[currentIdx]?.label || 'Not started'}</div>
            <div className="text-xs text-slate-500 mt-1">Last updated {project.build_status_updated_at ? new Date(project.build_status_updated_at).toLocaleString() : '-'}</div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-violet-600">{pct}%</div>
            <div className="text-xs text-slate-500">complete</div>
          </div>
        </div>
        <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h4 className="text-sm font-semibold text-slate-900 mb-4">Timeline</h4>
        <div className="space-y-3" data-testid="progress-timeline">
          {BUILD_STAGES.map((s, i) => {
            const done = i < currentIdx;
            const active = i === currentIdx;
            return (
              <div key={s.id} className="flex items-center gap-3">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${done ? 'bg-emerald-500 text-white' : active ? 'bg-violet-600 text-white ring-4 ring-violet-200' : 'bg-slate-200 text-slate-500'}`}>
                  {done ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                <div className={`text-sm ${active ? 'font-bold text-slate-900' : done ? 'text-slate-700' : 'text-slate-500'}`}>{s.label}</div>
                {!isClient && !active && (
                  <button onClick={() => setStatus(s.id)} className="ml-auto text-[10px] text-violet-600 hover:underline" data-testid={`progress-set-${s.id}`}>Set as current</button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// 7. LAUNCH TAB
// ────────────────────────────────────────────────────────────────
function LaunchTab({ project, firmId, onChange, isClient }) {
  const launch = project.launch || {};
  const [form, setForm] = useState(launch);
  const [showFeedback, setShowFeedback] = useState(false);
  useEffect(() => { setForm(launch); /* eslint-disable-next-line */ }, [project.id, launch.staging_url, launch.final_url]);

  const save = async () => {
    await axios.patch(`${API}/website/${firmId}/launch`, {
      staging_url: form.staging_url || '',
      final_url: form.final_url || '',
      launch_date: form.launch_date || null,
      notes: form.notes || ''
    }, { headers: authHeaders() });
    onChange();
  };
  const approve = async () => { await axios.post(`${API}/website/${firmId}/launch/approve`, {}, { headers: authHeaders() }); onChange(); };
  const sendFeedback = async (text) => {
    await axios.post(`${API}/website/${firmId}/launch/feedback`, { feedback: text }, { headers: authHeaders() });
    setShowFeedback(false); onChange();
  };

  return (
    <div className="space-y-4" data-testid="website-launch-tab">
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h4 className="text-sm font-semibold text-slate-900">Launch Details</h4>
          <ApprovalActions status={launch.status} feedback={launch.feedback}
            onApprove={approve} onFeedback={() => setShowFeedback(true)} isClient={isClient} />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Staging URL</label>
            {isClient ? (
              <div className="mt-2 flex items-center gap-2 px-4 py-2.5 bg-slate-50 rounded-xl text-sm">
                {launch.staging_url ? <a href={launch.staging_url} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline inline-flex items-center gap-1">{launch.staging_url} <ExternalLink className="w-3 h-3" /></a> : <span className="text-slate-400">Not yet provided</span>}
              </div>
            ) : (
              <input value={form.staging_url || ''} onChange={(e) => setForm({ ...form, staging_url: e.target.value })} type="url"
                className="mt-2 w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm" data-testid="launch-staging-url" />
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Final Website URL</label>
            {isClient ? (
              <div className="mt-2 flex items-center gap-2 px-4 py-2.5 bg-slate-50 rounded-xl text-sm">
                {launch.final_url ? <a href={launch.final_url} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline inline-flex items-center gap-1">{launch.final_url} <ExternalLink className="w-3 h-3" /></a> : <span className="text-slate-400">Not yet live</span>}
              </div>
            ) : (
              <input value={form.final_url || ''} onChange={(e) => setForm({ ...form, final_url: e.target.value })} type="url"
                className="mt-2 w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm" data-testid="launch-final-url" />
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Launch Date</label>
            {isClient ? (
              <div className="mt-2 px-4 py-2.5 bg-slate-50 rounded-xl text-sm">{launch.launch_date ? new Date(launch.launch_date).toLocaleDateString() : <span className="text-slate-400">TBD</span>}</div>
            ) : (
              <input type="date" value={form.launch_date ? form.launch_date.slice(0, 10) : ''}
                onChange={(e) => setForm({ ...form, launch_date: e.target.value ? new Date(e.target.value).toISOString() : null })}
                className="mt-2 w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm" />
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Notes</label>
            {isClient ? (
              <div className="mt-2 px-4 py-2.5 bg-slate-50 rounded-xl text-sm whitespace-pre-wrap">{launch.notes || <span className="text-slate-400">No notes yet.</span>}</div>
            ) : (
              <textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
                className="mt-2 w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm resize-none" />
            )}
          </div>
        </div>

        {!isClient && (
          <div className="flex justify-end">
            <button onClick={save} className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium" data-testid="launch-save-btn">Save</button>
          </div>
        )}
      </div>

      {showFeedback && <FeedbackModal onClose={() => setShowFeedback(false)} onSubmit={sendFeedback} title="Suggest launch changes" />}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// MAIN
// ────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'brief', label: 'Project Brief', icon: FileText },
  { id: 'assets', label: 'Assets', icon: ImageIcon },
  { id: 'sitemap', label: 'Sitemap', icon: ListTree },
  { id: 'design', label: 'Design', icon: Palette },
  { id: 'content', label: 'Content', icon: FileEdit },
  { id: 'progress', label: 'Build Progress', icon: Activity },
  { id: 'launch', label: 'Launch', icon: Rocket }
];

export default function WebsiteCreation({ user, selectedClient }) {
  const firmId = selectedClient?.firmId || user.firm?.id;
  const isClient = user.role === 'client';
  const [project, setProject] = useState(null);
  const [tab, setTab] = useState('brief');
  const [loading, setLoading] = useState(true);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [addonState, setAddonState] = useState(null); // 'enabled' | 'locked' | etc.

  const refreshAddonState = async () => {
    if (!firmId) return null;
    try {
      const res = await axios.get(`${API}/me/billing`, { headers: authHeaders() });
      const data = res.data || {};
      const ac = data.plan_config?.addons_config || {};
      // The dedicated Website Creation purchase flag wins; fall back to the
      // legacy addon-config flag, then Super Admin override.
      let st;
      if (data.website_plan_active) st = 'enabled';
      else if (ac.website_creation === 'enabled') st = 'enabled';
      else if (data.manual_override_enabled) st = 'enabled';
      else st = ac.website_creation || 'locked';
      setAddonState(st);
      return st;
    } catch (_e) { return null; }
  };

  const refresh = async () => {
    if (!firmId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/website/${firmId}`, { headers: authHeaders() });
      setProject(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); refreshAddonState(); /* eslint-disable-next-line */ }, [firmId]);

  // Post-Stripe-checkout polling. If the user clicked Choose Plan recently and
  // the add-on isn't active yet, keep polling /me/billing every 3s for up to
  // 60s so the dashboard reacts the moment the webhook lands.
  useEffect(() => {
    if (!firmId || !isClient) return undefined;
    if (addonState === 'enabled') return undefined;
    let stamp;
    try { stamp = localStorage.getItem('website_creation_paying_at'); } catch (_e) { stamp = null; }
    if (!stamp) return undefined;
    const ageMs = Date.now() - new Date(stamp).getTime();
    if (Number.isNaN(ageMs) || ageMs > 1000 * 60 * 30) return undefined; // >30 min: give up
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      const st = await refreshAddonState();
      if (st === 'enabled') {
        try { localStorage.removeItem('website_creation_paying_at'); } catch (_e) { /* ignore */ }
        return;
      }
      if (attempts >= 20) return;
      setTimeout(tick, 3000);
    };
    tick();
    return () => { cancelled = true; };
  }, [firmId, isClient, addonState]);

  // After Stripe redirect or manual unlock: if the add-on is now enabled and we
  // saved a pending brief, auto-submit it and clear the cached draft.
  useEffect(() => {
    if (!firmId || !isClient) return;
    if (addonState !== 'enabled') return;
    let cached;
    try {
      cached = localStorage.getItem(`website_creation_pending_brief:${firmId}`);
    } catch (_e) { return; }
    if (!cached) return;
    (async () => {
      try {
        const { brief } = JSON.parse(cached);
        if (brief) {
          await axios.patch(`${API}/website/${firmId}/brief`, brief, { headers: authHeaders() });
        }
        await axios.post(`${API}/website/${firmId}/brief/submit`, {}, { headers: authHeaders() });
        localStorage.removeItem(`website_creation_pending_brief:${firmId}`);
        refresh();
      } catch (_e) {
        // leave the cached brief in place so the user can retry manually
      }
    })();
  }, [addonState, firmId, isClient]);

  const seed = async () => {
    try { await axios.post(`${API}/website/seed/${firmId}`, {}, { headers: authHeaders() }); refresh(); }
    catch (e) { console.error(e); }
  };

  if (loading || !project) return <div className="text-center py-10 text-slate-500">Loading website project...</div>;

  const addonEnabled = addonState === 'enabled' || !isClient; // staff/admin always allowed

  return (
    <div className="space-y-6" data-testid="website-creation-module">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-md shadow-violet-500/30">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Website Creation</h1>
            <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold uppercase tracking-wide">Add-on</span>
          </div>
          <p className="text-sm text-slate-500">Plan, design and ship a website your firm can be proud of - with full transparency at every stage.</p>
        </div>
        {!isClient && (project.designs || []).length === 0 && (
          <button onClick={seed} className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-600 hover:bg-slate-50" data-testid="website-seed-btn">
            <Sparkles className="w-3.5 h-3.5 inline mr-1" /> Load sample data
          </button>
        )}
      </div>

      <AddonEntitlements firmId={firmId} addonKey="website_creation" />

      <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1 overflow-x-auto" data-testid="website-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${tab === t.id ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-md' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid={`website-tab-${t.id}`}>
              <Icon className="w-4 h-4" />{t.label}
            </button>
          );
        })}
      </div>

      {tab === 'brief' && <BriefTab project={project} firmId={firmId} onChange={refresh} isClient={isClient} addonEnabled={addonEnabled} onPaywall={() => setPaywallOpen(true)} />}
      {tab === 'assets' && <AssetsTab project={project} firmId={firmId} onChange={refresh} isClient={isClient} />}
      {tab === 'sitemap' && <SitemapTab project={project} firmId={firmId} onChange={refresh} isClient={isClient} />}
      {tab === 'design' && <DesignTab project={project} firmId={firmId} onChange={refresh} isClient={isClient} />}
      {tab === 'content' && <ContentTab project={project} firmId={firmId} onChange={refresh} isClient={isClient} />}
      {tab === 'progress' && <BuildProgressTab project={project} firmId={firmId} onChange={refresh} isClient={isClient} />}
      {tab === 'launch' && <LaunchTab project={project} firmId={firmId} onChange={refresh} isClient={isClient} />}

      <WebsitePaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        onSavePendingBrief={() => { /* draft already saved in BriefTab.submit() */ }}
      />
    </div>
  );
}
